import React, { useEffect, useState } from "react";
import axios from "axios";
import { DateTime } from "luxon";
import Swal from "sweetalert2";

import API_URL from "../config";

const capitalizar = (texto) =>
  texto
    ? texto.charAt(0).toUpperCase() + texto.slice(1)
    : "";

/*1 =========================================
   🎨 COLORES SEGÚN ESTADO
========================================= */
const obtenerEstadoVisual = (cita) => {

  if (cita.estado === "atendida") {
    return {
      texto: "Atendida",
      fondo: "#DCFCE7",
      color: "#16A34A",
    };
  }

  if (cita.estado === "no_asistio") {
    return {
      texto: "No asistió",
      fondo: "#FEE2E2",
      color: "#DC2626",
    };
  }

  if (cita.estado === "cancelada_admin") {
    return {
      texto: "Cancelada por admin",
      fondo: "#FEE2E2",
      color: "#B91C1C",
    };
  }

  if (cita.estado === "cancelada_profesor") {
    return {
      texto: "Cancelada por profesor",
      fondo: "#FEF3C7",
      color: "#D97706",
    };
  }

  if (cita.estado === "pendiente") {
    return {
      texto: "Pendiente",
      fondo: "#DCFCE7",
      color: "#16A34A",
    };
  }

  return {
    texto: "Sin estado",
    fondo: "#F3F4F6",
    color: "#374151",
  };
};

const ListaCitasProfesor = () => {

  const [citas, setCitas] = useState([]);
  const [mostrarHistorial, setMostrarHistorial] =
    useState(false);

  const [profesorId, setProfesorId] =
    useState(null);

  const [paginaActual, setPaginaActual] = useState(1);
  const citasPorPagina = 5;

  /*2 =========================================
     📅 FORMATO FECHA
  ========================================= */
  const formatFecha = (fecha) =>
    fecha
      ? DateTime.fromISO(fecha)
          .setLocale("es")
          .toFormat("dd 'de' LLLL yyyy")
      : "—";

  /*3 =========================================
     🕒 FORMATO HORA
  ========================================= */
  const formatHora = (hora) =>
    hora
      ? DateTime.fromFormat(
          hora,
          "HH:mm:ss"
        ).toFormat("hh:mm a")
      : "—";

  /*3c =========================================
     ⏳ ¿SE PUEDE CANCELAR? (hasta 12h antes de la cita)
  ========================================= */
  const LIMITE_CANCELACION_MIN = 12 * 60;

  const puedeCancelar = (cita) => {
    const fechaLocal = DateTime.fromISO(cita.fecha, { zone: "utc" })
      .setZone("America/Managua");

    const inicioCita = DateTime.fromISO(
      `${fechaLocal.toFormat("yyyy-MM-dd")}T${cita.hora_inicio}`,
      { zone: "America/Managua" }
    );

    const diffMinutos = inicioCita.diff(
      DateTime.now().setZone("America/Managua"),
      "minutes"
    ).minutes;

    return diffMinutos >= LIMITE_CANCELACION_MIN;
  };

  /*4 =========================================
     🔵 CARGAR CITAS
  ========================================= */
  const cargar = async () => {

    try {

      const usuario = JSON.parse(
        localStorage.getItem("usuario")
      );

      if (!usuario?.id) return;

    
const profRes = await axios.get(
  `${API_URL}/api/profesores/por-usuario/${usuario.id}`
);

      const id = profRes.data.id;

      setProfesorId(id);

    
const citasRes = await axios.get(
  `${API_URL}/api/citas/profesor/${id}`
);

      setCitas(citasRes.data || []);

    } catch (err) {

      console.error(err);
    }
  };

  useEffect(() => {

    cargar();

  }, []);

  /* 5=========================================
     🔄 RECARGAR
  ========================================= */
  const recargar = async () => {

    if (!profesorId) return;

  
const res = await axios.get(
  `${API_URL}/api/citas/profesor/${profesorId}`
);

    setCitas(res.data || []);
  };

  /*6 =========================================
     ❌ CANCELAR CITA (hasta 12h antes)
  ========================================= */
  const cancelarCita = async (cita) => {

    if (!puedeCancelar(cita)) {
      Swal.fire({
        icon: "info",
        title: "Ya no puedes cancelarla tú",
        text: "Solo puedes cancelar la cita hasta 12 horas antes. Pasado ese tiempo, si quieres cancelarla contacta al administrador.",
      });
      return;
    }

    const ok = await Swal.fire({
      title: "¿Cancelar esta cita?",
      text: "Se notificará al padre.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cancelar",
    });

    if (!ok.isConfirmed) return;

    try {

await axios.post(
  `${API_URL}/api/citas/profesor/cancelar/${cita.id}`
);

      Swal.fire({
        icon: "success",
        title: "Cita cancelada",
        text: "Se notificó al padre.",
      });

      recargar();

    } catch (err) {

      console.error(err);

      Swal.fire({
        icon: "error",
        title: "No se pudo cancelar",
        text: err.response?.data?.msg || err.response?.data?.error || "Ocurrió un problema",
      });
    }
  };

  /* 8=========================================
     📚 FILTRO
  ========================================= */
  const citasFiltradas = citas.filter(
    (c) => {

      const activas =
        c.estado === "pendiente";

     const historial =
  c.estado === "atendida" ||
  c.estado === "no_asistio" ||
  c.estado === "cancelada_admin" ||
  c.estado === "cancelada_profesor";

      // El historial solo muestra citas del año en curso — el año que viene
      // ya no aparecen las de este año.
      const esDelAnoActual = c.fecha && DateTime.fromISO(c.fecha).year === DateTime.now().year;

      return mostrarHistorial
        ? historial && esDelAnoActual
        : activas;
    }
  ).sort((a, b) => {
    const claveA = `${a.fecha}${a.hora_inicio}`;
    const claveB = `${b.fecha}${b.hora_inicio}`;
    const diff = claveA.localeCompare(claveB);

    // Pendientes: la más próxima primero. Historial: la más reciente primero.
    return mostrarHistorial ? -diff : diff;
  });

  /* 8b ========================================
     📄 PAGINACIÓN (5 por página)
  ========================================= */
  const totalPaginas = Math.ceil(citasFiltradas.length / citasPorPagina);

  const citasPaginadas = citasFiltradas.slice(
    (paginaActual - 1) * citasPorPagina,
    paginaActual * citasPorPagina
  );

  useEffect(() => {
    if (totalPaginas > 0 && paginaActual > totalPaginas) {
      setPaginaActual(totalPaginas);
    }
  }, [totalPaginas, paginaActual]);

  return (

    <div className="w-full max-w-3xl mx-auto p-3 sm:p-6">

      {/*9 =====================================
          HEADER
      ===================================== */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">

        <div>

          <h1 className="text-4xl font-bold text-green-700">
            Gestión de Citas
          </h1>

          <p className="text-gray-500 mt-2">
            Administra tus citas y revisa
            tu historial.
          </p>

        </div>

        <button
          onClick={() => {
            setMostrarHistorial(!mostrarHistorial);
            setPaginaActual(1);
          }}
          className="bg-green-600 hover:bg-green-700 text-white px-5 py-3 rounded-xl shadow font-semibold transition"
        >
          {mostrarHistorial
            ? "📌 Ver pendientes"
            : "📚 Ver historial"}
        </button>

      </div>

      {/*10 =====================================
          SIN CITAS
      ===================================== */}
      {citasFiltradas.length === 0 && (

        <div className="bg-white border rounded-2xl shadow p-10 text-center text-gray-500 text-lg">
          No hay citas para mostrar.
        </div>
      )}

      {/*11 =====================================
          TARJETAS
      ===================================== */}
      <div className="grid gap-6">

        {citasPaginadas.map((cita) => {

          const est =
            obtenerEstadoVisual(cita);

          return (

            <div
              key={cita.id}
              className={`bg-white border-2 ${est.borde} rounded-3xl shadow-lg p-6 hover:shadow-2xl transition`}
            >

              {/*12 ============================
                  ESTADO
              ============================ */}
            <div className="flex justify-between items-start mb-4">

  <div
    style={{
      background: est.fondo,
      color: est.color,
    }}
    className="px-4 py-2 rounded-full font-bold text-sm"
  >
    {est.texto}
  </div>

</div>

              {/*13 ============================
                  INFO
              ============================ */}
              <div className="space-y-3 text-gray-700">

                <p>
                  👦{" "}
                  <span className="font-semibold">
                    Nombre del Alumno:
                  </span>{" "}
                  {capitalizar(
                    cita.alumno_nombre
                  )}
                </p>

                <p>
                  🎒{" "}
                  <span className="font-semibold">
                    Grado:
                  </span>{" "}
                  {cita.grado}
                </p>

                <p>
                  👨‍🏫{" "}
                  <span className="font-semibold">
                    Profesor:
                  </span>{" "}
                  {capitalizar(
                    cita.profesor_nombre
                  )}
                </p>

                <p>
                  📅{" "}
                  <span className="font-semibold">
                    Fecha:
                  </span>{" "}
                  {formatFecha(cita.fecha)}
                </p>

                <p>
                  🕒{" "}
                  <span className="font-semibold">
                    Hora:
                  </span>{" "}
                  {formatHora(
                    cita.hora_inicio
                  )}{" "}
                  -{" "}
                  {formatHora(
                    cita.hora_fin
                  )}
                </p>

                <p>
                  📧{" "}
                  <span className="font-semibold">
                    Correo:
                  </span>{" "}
                  {cita.correo_padre}
                </p>

                {cita.nota && (

                  <div className="bg-gray-50 border rounded-xl p-4 mt-3">

                    <p className="font-semibold text-gray-800 mb-1">
                      💬 Nota:
                    </p>

                    <p className="text-gray-600">
                      {cita.nota}
                    </p>

                  </div>
                )}

              </div>

              {/*14 ============================
                  ACCIONES
              ============================ */}
              {!mostrarHistorial &&
                cita.estado ===
                  "pendiente" && (

                <div className="mt-6">

                  <div className="flex flex-wrap gap-3">

                    <button
                      onClick={() => cancelarCita(cita)}
                      className={`px-5 py-3 rounded-xl font-semibold transition text-white ${
                        puedeCancelar(cita)
                          ? "bg-red-600 hover:bg-red-700 cursor-pointer"
                          : "bg-red-600/40 cursor-not-allowed"
                      }`}
                    >
                      ✖ Cancelar cita
                    </button>

                  </div>

                  <p className="mt-3 text-sm text-gray-500 italic">
                    Puedes cancelar la cita hasta 12 horas antes de la hora programada. Pasado ese tiempo, contacta al administrador.
                  </p>

                </div>
              )}

            </div>
          );
        })}
      </div>

      {/*15 =====================================
          PAGINACIÓN
      ===================================== */}
      {totalPaginas > 1 && (
        <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
          <button
            disabled={paginaActual === 1}
            onClick={() => setPaginaActual((p) => p - 1)}
            className="px-3 py-2 rounded-lg font-semibold bg-gray-200 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ‹ Anterior
          </button>

          {Array.from({ length: totalPaginas }, (_, i) => (
            <button
              key={i}
              onClick={() => setPaginaActual(i + 1)}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                paginaActual === i + 1
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={paginaActual === totalPaginas}
            onClick={() => setPaginaActual((p) => p + 1)}
            className="px-3 py-2 rounded-lg font-semibold bg-gray-200 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Siguiente ›
          </button>
        </div>
      )}
    </div>
  );
};

export default ListaCitasProfesor;